import * as React from "react";

import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import type { createDeviceForm, DeviceKind } from "../../../lib/apis/dtos/request/device.request";
import { deviceErrorMessage, invalidateDeviceQueries } from "../../../lib/apis/device-cache";
import { createDevice } from "../../../lib/apis/v1/device.service";
import FailSnackbar from "../../ui/snackbar/fail.component";
import { getQueryClient } from "../../../config/queryClient.config";
import SuccessSnackbar from "../../ui/snackbar/success.component";
import ConfirmModal from "../confirm.modal";
import Button from "../../ui/button";
import { cn } from "../../../utils/cn.utils";
import type { ModalComponentProps } from "../../../types/modal.types";

const DEVICE_MODEL = [
  { label: "아이폰", value: "IPHONE" },
  { label: "갤럭시", value: "GALAXY" },
  { label: "기타", value: "ETC" },
];

const CATEGORY_TYPE = [
  { label: "주요폰", value: "main" },
  { label: "중저가폰", value: "low" },
  { label: "키즈폰", value: "kids" },
];

export default function DeviceCreateModal(props: ModalComponentProps) {
  const { id, onClose } = props;

  const [form, setForm] = React.useState<Omit<createDeviceForm, "category" | "storages"> & {
    category: DeviceKind | "";
    storages: { storage: string; price: string }[];
  }>({
    deviceCode: "",
    deviceName: "",
    category: "",
    mainCheck: false,
    lowCheck: false,
    kidsCheck: false,
    storages: [{ storage: "", price: "" }],
    isDefault: false,
    isOnEvent: false,
    eventTag: "",
    eventMessage: ""
  });

  const deviceMutation = useMutation({
    mutationFn: (form: createDeviceForm) => createDevice(form),
    onSuccess: (res) => {
      const { error, message } = res || {};
      if (error) {
        return FailSnackbar(`기종 생성 실패 (${message})`)
      }
      void invalidateDeviceQueries(getQueryClient());
      SuccessSnackbar("기종 생성 완료");
      onClose?.();
    },
    onError: (error) => FailSnackbar(deviceErrorMessage(error)),
  });

  return (
    <ConfirmModal
      title="기종 생성"
      rightButton={{
        title: "생성",
        disabled: deviceMutation.isPending || !form.deviceName.trim() || !form.deviceCode?.trim() || !form.category
          || form.storages.some(({ storage, price }) => !storage.trim() || price === ""
            || !Number.isInteger(Number(price)) || Number(price) < 0 || Number(price) > 2147483647),
        onClick: () => {
          if (deviceMutation.isPending || !form.category) return;
          const storages = form.storages.map(({ storage, price }) => ({
            storage: storage.trim(), price: Number(price),
          }));
          const _form: createDeviceForm = {
            ...form,
            category: form.category,
            deviceName: form.deviceName.trim(),
            deviceCode: form.deviceCode?.trim(),
            storages,
          };
          const uniqueDeplicates = new Set(storages.map(({ storage }) => storage));
          if (uniqueDeplicates.size !== storages.length) {
            return FailSnackbar("기종 생성 실패 (중복되는 용량이 있습니다.)");
          }

          deviceMutation.mutate(_form);
        },
      }}
      id={id}
      onClose={onClose}
    >
      <p className="text-[16px]">카테고리 구분 (복수 선택 가능)</p>
      <FormGroup className="px-[9px]" row>
        {CATEGORY_TYPE.map(({ label, value }, index) => {
          const key = `${value}Check` as keyof typeof form;
          return (
            <FormControlLabel
              key={index}
              control={
                <Checkbox
                  onClick={() =>
                    setForm((state) => ({
                      ...state,
                      [`${value}Check`]: !state[key],
                    }))
                  }
                />
              }
              label={label}
            />
          );
        })}
      </FormGroup>
      <p className="text-[16px] mt-[16px]">모델 구분</p>
      <RadioGroup
        className="px-[9px]"
        row
        aria-labelledby="demo-radio-bujttons-group-label"
        name="radio-buttons-group"
      >
        {DEVICE_MODEL.map(({ label, value }, index) => (
          <FormControlLabel
            key={index}
            value={value}
            control={
              <Radio
                onClick={() =>
                  setForm((state) => ({ ...state, category: value as DeviceKind }))
                }
              />
            }
            label={label}
          />
        ))}
      </RadioGroup>
      <div className="mt-[16px] flex flex-col items-center justify-start gap-4">
        <TextField
          className="w-[552px] h-[52px]"
          label="기종명*"
          placeholder="기종명 입력"
          onChange={(e) =>
            setForm((state) => ({ ...state, deviceName: e.target.value }))
          }
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          className="w-[552px] h-[52px]"
          label="모델 고유번호*"
          placeholder="모델 고유번호 입력"
          onChange={(e) =>
            setForm((state) => ({ ...state, deviceCode: e.target.value }))
          }
          slotProps={{ inputLabel: { shrink: true } }}
        />
        {form.storages.map((item, index) => {
          return (
            <div className="flex-row gap-3" key={index}>
              <TextField
                className={cn("h-[52px]", "w-[240px]")}
                label="용량(GB)"
                placeholder="용량 입력 (ex. 256GB)"
                value={item.storage}
                onChange={(e) =>
                  setForm((state) => {
                    const updated = [...state.storages];
                    updated[index] = { ...updated[index], storage: e.target.value };
                    return {
                      ...state,
                      storages: updated,
                    };
                  })
                }
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                className="w-[240px]"
                label="출고가(원)*"
                type="number"
                value={item.price}
                slotProps={{ htmlInput: { min: 0, max: 2147483647, step: 1 } }}
                onChange={(e) => setForm((state) => ({
                  ...state,
                  storages: state.storages.map((storage, i) => i === index
                    ? { ...storage, price: e.target.value } : storage),
                }))}
              />
              {index !== 0 && (
                <img className="cursor-pointer" onClick={() => {
                  setForm((state) => ({ ...state, storages: state.storages.filter((_, i) => i !== index) }))
                }} width={24} height={24} alt="" src={"/static/icons/icon_cancel_fill.svg"} />
              )}
            </div>

          );
        })}
        <Button
          label="용량 추가"
          className="w-full"
          variant={"outline"}
          onClick={() =>
            setForm((state) => ({
              ...state,
              storages: [...state.storages, { storage: "", price: "" }],
            }))
          }
        />
      </div>
    </ConfirmModal>
  );
};
